import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { AppLayout } from "@/components/AppLayout";
import { motion } from "framer-motion";
import { Home, ChevronRight, FileText } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HouseholdList = ({ user }) => {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadConfigs = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/configs`, { withCredentials: true });
        setConfigs(response.data);
      } catch (error) {
        toast.error('Failed to load households');
      } finally {
        setLoading(false);
      }
    };
    loadConfigs();
  }, []);

  return (
    <AppLayout user={user} activeNav="/households">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <div className="mb-8">
          <h2 className="text-2xl font-heading font-extrabold text-stone-900">Households</h2>
          <p className="text-stone-500 text-sm mt-1">View order history for each household</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-sage-500 border-t-transparent mx-auto"></div>
          </div>
        ) : configs.length === 0 ? (
          <Card className="bg-white/90 backdrop-blur-sm">
            <CardContent className="py-12 text-center">
              <Home className="w-14 h-14 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500 text-lg">No households yet</p>
              <p className="text-sm text-stone-400 mt-1">Create a configuration to set up a household</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {configs.map((config, i) => (
              <motion.div key={config.config_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <button
                  onClick={() => navigate(`/households/${config.config_id}`)}
                  className="w-full text-left"
                  data-testid={`household-card-${config.config_id}`}
                >
                  <Card className="bg-white/90 backdrop-blur-sm border border-stone-200/60 hover:shadow-md hover:border-sage-200 transition-all cursor-pointer group">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-sage-50 rounded-xl flex items-center justify-center group-hover:bg-sage-100 transition">
                            <Home className="w-5 h-5 text-sage-600" />
                          </div>
                          <div>
                            <p className="font-heading font-bold text-stone-800">{config.drive_folder_name}</p>
                            {config.sheet_tab_name ? (
                              <p className="text-xs text-stone-400 mt-0.5 flex items-center gap-1">
                                <FileText className="w-3 h-3" /> {config.sheet_tab_name}
                              </p>
                            ) : null}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-stone-300 group-hover:text-sage-500 transition" />
                      </div>
                    </CardContent>
                  </Card>
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>
    </AppLayout>
  );
};

export default HouseholdList;
